import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import projects from '../content/projects.json';
import writing from '../content/writing.json';
import { PageMeta } from './SiteLayout';
import { NotFoundPage } from './PortfolioPages';

function ContentLink({ node, href = '', children, ...props }) {
    if (href.startsWith('/') && !href.startsWith('//') && !/\.(?:pdf|zip|xml)$/.test(href)) return <Link to={href} {...props}>{children}</Link>;
    return <a href={href} {...props}>{children}</a>;
}

const headingId = children => React.Children.toArray(children).filter(child => typeof child === 'string').join(' ').toLowerCase().replace(/^the /, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function ArticleImage({ zoom, alt = '', ...props }) {
    const img = <img {...props} alt={alt} />;
    return zoom ? <a className="image-full" href={props.src} target="_blank" rel="noopener noreferrer" aria-label={`Open full-size image: ${alt}`}>{img}</a> : img;
}

export default function ArticlePage({ kind }) {
    const { slug } = useParams();
    const { hash } = useLocation();
    const isProject = kind === 'project';
    const entry = (isProject ? projects : writing).find(item => item.slug === slug);
    const [content, setContent] = useState({ key: '', text: '', error: false });
    const contentKey = `${kind}/${slug}`;
    useEffect(() => {
        let active = true;
        if (!entry) return undefined;
        const load = isProject ? import(`../project_posts/${slug}.md`) : import(`../blog_posts/${slug}.md`);
        load.then(module => fetch(module.default)).then(response => {
            if (!response.ok) throw new Error('Article unavailable');
            return response.text();
        }).then(text => {
            if (active) setContent({ key: contentKey, text: text.replace(/^# [^\n]*\n/, ''), error: false });
        }).catch(() => { if (active) setContent({ key: contentKey, text: '', error: true }); });
        return () => { active = false; };
    }, [slug, isProject, entry, contentKey]);
    useEffect(() => {
        if (hash && content.key === contentKey) {
            requestAnimationFrame(() => document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView());
        }
    }, [hash, content, contentKey]);
    if (!entry) return <NotFoundPage />;
    const zoomImages = isProject && ['godswork', 'huyasno'].includes(slug);
    const extraLinks = isProject && content.key === contentKey ? (entry.links || []).filter(link => !content.text.includes(link.href)) : [];
    const related = isProject && entry.showRelatedWriting !== false ? writing.filter(post => post.href !== `/projects/${slug}` && (post.project === slug || entry.relatedWriting?.includes(post.slug))) : [];
    return <article className="article-page">
        <PageMeta title={entry.title} description={entry.summary} image={entry.image?.src} type="article" />
        <Link className="back-link" to={isProject ? '/projects' : '/writing'}>{isProject ? 'Projects' : 'Writing'}</Link>
        <header className="article-heading">
            <p className="eyebrow">{isProject ? [entry.category, entry.period].filter(Boolean).join(' / ') : entry.label}</p>
            <h1>{entry.title}</h1>
            {isProject && <p className="article-deck">{entry.summary}</p>}
            {isProject && entry.role && <p className="article-role">{entry.role}</p>}
        </header>
        {isProject && entry.image && <figure className="article-cover"><ArticleImage zoom={zoomImages} src={entry.image.src} alt={entry.image.alt || entry.title} width="1200" height="700" />{entry.image.caption && <figcaption>{entry.image.caption}</figcaption>}</figure>}
        {content.key !== contentKey ? <p role="status" className="article-loading">Loading…</p> : content.error ? <p role="alert">Couldn't load this piece. <button onClick={() => window.location.reload()}>Try again</button>.</p> : <div className="article-body"><ReactMarkdown rehypePlugins={[rehypeRaw]} components={{
            a: ContentLink,
            h2: ({ node, children, ...props }) => <h2 id={headingId(children)} {...props}>{children}</h2>,
            img: ({ node, ...props }) => <ArticleImage {...props} zoom={zoomImages} loading="lazy" />,
            iframe: ({ node, ...props }) => <iframe {...props} loading="lazy" title={props.title || entry.title} />,
        }}>{content.text}</ReactMarkdown></div>}
        {extraLinks.length > 0 && <div className="article-links">{extraLinks.map(link => <a key={link.href} href={link.href}>{link.label}</a>)}</div>}
        {related.length > 0 && <aside className="related-writing"><h2>More about this</h2>{related.map(post => <Link key={post.slug} to={post.href || `/posts/${post.slug}`}>{post.title}</Link>)}</aside>}
        {!isProject && entry.project && projects.some(p => p.slug === entry.project) && <aside className="related-writing"><Link to={`/projects/${entry.project}`}>More about the project</Link></aside>}
    </article>;
}
